/**
 * El carrito, en `localStorage`.
 *
 * NO vive en el servidor, y es deliberado: un carrito no necesita durar entre
 * dispositivos, y guardarlo en la base añade una tabla y una escritura por cada
 * clic en "Agregar" a cambio de un beneficio que nadie pidió. Lo que sí es del
 * servidor es la CIFRA: el total vinculante viene siempre de
 * `POST /api/carrito/cotizar`, que recalcula precio, flete e IVA leyendo costos
 * y tarifas de la base. Acá solo se guarda la INTENCIÓN —qué ARTÍCULO y cuánto—,
 * que es exactamente lo que la API acepta del cliente.
 *
 * Guarda el artículo y nunca la oferta: de qué distribuidor se compra lo decide
 * el servidor al emitir, y el navegador no conoce ni puede nombrar las ofertas.
 *
 * Store mínimo sobre `useSyncExternalStore`, sin librería de estado: son dos
 * campos y cinco acciones. Lo único que hay que hacer bien es devolver una
 * instantánea ESTABLE —React compara por identidad y volvería a renderizar sin
 * parar si cada lectura parseara el JSON de nuevo— y escuchar el evento
 * `storage` para que dos pestañas del mismo navegador no se contradigan.
 */
import { useEffect, useSyncExternalStore } from 'react'
import { ErrorApi } from '../api/cliente'

export interface ItemCarrito {
  articuloId: string
  cantidad: number
}

export interface EstadoCarrito {
  items: ItemCarrito[]
  /** Faena de destino elegida. `null` mientras no se elige. */
  faenaId: string | null
  /**
   * Operador logístico que retira la carga. `null` mientras no se elige.
   *
   * Es el andén donde entregamos: nuestro transporte llega a él y la minera
   * hace el último tramo hasta la faena. Son dos puntos distintos, y por eso
   * son dos campos y no uno derivado del otro.
   *
   * NO se sube la versión de la clave por agregarlo: un carrito guardado por la
   * versión anterior no trae el campo, `normalizar` lo deja en `null` y eso es
   * exactamente lo correcto —«todavía no eligió»—. Subir la versión tiraría a
   * la basura el carrito de todo el mundo para agregar un campo opcional.
   */
  operadorId: string | null
}

/*
 * La versión va en la clave, no dentro del valor: si mañana la forma cambia, la
 * clave nueva simplemente no encuentra nada y el carrito arranca vacío, que es
 * mucho mejor que migrar a ciegas un JSON escrito por una versión anterior.
 *
 * Se subió a v2 al pasar de guardar productos a guardar artículos. Es justo el
 * caso que esta convención anticipaba: un carrito v1 lleva ids de OFERTA, y
 * mandarlos como si fueran artículos habría producido pedidos que a veces
 * resolvían por coincidencia y a veces fallaban sin explicación.
 */
const CLAVE = 'compranorte.carrito.v2'

const VACIO: EstadoCarrito = { items: [], faenaId: null, operadorId: null }

/** Tope por línea, el mismo que valida el contrato de la API. */
const CANTIDAD_MAXIMA = 100_000

/**
 * Largo de un identificador, el mismo que exige el contrato de la API
 * (`z.string().trim().length(36)`): UUID v7 en `char(36)`.
 *
 * Ni más estricto ni más laxo que el servidor a propósito. Más estricto
 * rechazaría un id válido; más laxo es lo que dejaba salir una faena con forma
 * imposible en `?faenaId=` del catálogo.
 */
export const LARGO_ID = 36

/** ¿Tiene forma de identificador? No dice que exista: eso lo dice el servidor. */
export function pareceId(valor: string): boolean {
  return valor.trim().length === LARGO_ID
}

let cache: EstadoCarrito = VACIO
let cacheCruda: string | null = null
const oyentes = new Set<() => void>()

/**
 * Lee y normaliza lo guardado.
 *
 * Se valida campo por campo porque `localStorage` es texto que cualquiera puede
 * editar desde la consola del navegador: un `cantidad: "muchas"` que llegara
 * hasta la petición se convertiría en un 400 sin explicación. Lo que no calza
 * se descarta en silencio; el carrito no es el lugar para un mensaje de error.
 */
function normalizar(crudo: unknown): EstadoCarrito {
  if (typeof crudo !== 'object' || crudo === null) return VACIO
  const obj = crudo as Record<string, unknown>

  const items: ItemCarrito[] = []
  if (Array.isArray(obj.items)) {
    for (const it of obj.items) {
      if (typeof it !== 'object' || it === null) continue
      const linea = it as Record<string, unknown>
      const articuloId = linea.articuloId
      const cantidad = linea.cantidad
      if (typeof articuloId !== 'string' || articuloId === '') continue
      if (typeof cantidad !== 'number' || !Number.isInteger(cantidad) || cantidad <= 0) continue
      // Un artículo repetido descuadraría la reserva de stock y rompe el único
      // (ordenId, articuloId) de las líneas —dos líneas del mismo artículo
      // resuelven a la misma oferta—: la API lo rechaza, así que acá se colapsa
      // antes de llegar a mandarlo.
      const previo = items.find((l) => l.articuloId === articuloId)
      if (previo) previo.cantidad = Math.min(CANTIDAD_MAXIMA, previo.cantidad + cantidad)
      else items.push({ articuloId, cantidad: Math.min(CANTIDAD_MAXIMA, cantidad) })
    }
  }

  /*
   * La faena se valida por FORMA, igual que la cantidad. Una cadena cualquiera
   * viajaba en `?faenaId=` del catálogo, donde el contrato exige 36 caracteres,
   * y el 400 resultante dejaba la vitrina rota en toda visita siguiente: el
   * valor malo estaba guardado, así que recargar no arreglaba nada.
   */
  const faenaId =
    typeof obj.faenaId === 'string' && pareceId(obj.faenaId) ? obj.faenaId.trim() : null

  // Mismo criterio de forma que la faena, y por el mismo motivo: un valor con
  // forma imposible llega al servidor como un 400 sin explicación.
  const operadorId =
    typeof obj.operadorId === 'string' && pareceId(obj.operadorId) ? obj.operadorId.trim() : null

  return { items, faenaId, operadorId }
}

function leer(): EstadoCarrito {
  let crudo: string | null = null
  try {
    crudo = localStorage.getItem(CLAVE)
  } catch {
    // Modo privado de Safari, cookies de terceros bloqueadas, cuota llena: el
    // carrito funciona en memoria durante la sesión y no se persiste.
    return cache
  }

  // Misma cadena que la última vez ⇒ misma instantánea. Es lo que evita el
  // bucle de renderizado de `useSyncExternalStore`.
  if (crudo === cacheCruda) return cache

  cacheCruda = crudo
  if (!crudo) {
    cache = VACIO
    return cache
  }
  try {
    cache = normalizar(JSON.parse(crudo))
  } catch {
    cache = VACIO
  }
  return cache
}

function escribir(siguiente: EstadoCarrito): void {
  cache = siguiente
  const texto = JSON.stringify(siguiente)
  cacheCruda = texto
  try {
    localStorage.setItem(CLAVE, texto)
  } catch {
    // Sin persistencia, pero el estado en memoria ya se actualizó: la pantalla
    // responde igual y lo único que se pierde es la recarga.
  }
  for (const o of oyentes) o()
}

function suscribir(oyente: () => void): () => void {
  oyentes.add(oyente)
  /*
   * `storage` solo se dispara en las OTRAS pestañas del mismo origen, nunca en
   * la que escribió. Sin esto, dos pestañas abiertas divergen y la segunda
   * confirma una orden con lo que había hace media hora.
   */
  const desdeOtraPestana = (ev: StorageEvent) => {
    if (ev.key !== null && ev.key !== CLAVE) return
    cacheCruda = null
    leer()
    oyente()
  }
  window.addEventListener('storage', desdeOtraPestana)
  return () => {
    oyentes.delete(oyente)
    window.removeEventListener('storage', desdeOtraPestana)
  }
}

// ─── Lectura ────────────────────────────────────────────────────────────────

export function useCarrito(): EstadoCarrito {
  return useSyncExternalStore(suscribir, leer, () => VACIO)
}

/** Unidades totales, para el contador del encabezado. */
export function useUnidadesCarrito(): number {
  const { items } = useCarrito()
  return items.reduce((s, i) => s + i.cantidad, 0)
}

// ─── Escritura ──────────────────────────────────────────────────────────────

/**
 * Suma cantidad a una línea, o la crea.
 *
 * Suma y no reemplaza: quien pulsa "Agregar" tres veces desde la grilla espera
 * tres unidades, no una.
 */
export function agregar(articuloId: string, cantidad = 1): void {
  const actual = leer()
  const linea = actual.items.find((i) => i.articuloId === articuloId)
  const items = linea
    ? actual.items.map((i) =>
        i.articuloId === articuloId
          ? { ...i, cantidad: Math.min(CANTIDAD_MAXIMA, i.cantidad + cantidad) }
          : i,
      )
    : [...actual.items, { articuloId, cantidad: Math.min(CANTIDAD_MAXIMA, Math.max(1, cantidad)) }]
  escribir({ ...actual, items })
}

/** Fija la cantidad exacta de una línea. Cero o menos no borra: usa `quitar`. */
export function fijarCantidad(articuloId: string, cantidad: number): void {
  const actual = leer()
  const limpia = Math.min(CANTIDAD_MAXIMA, Math.max(1, Math.floor(cantidad) || 1))
  escribir({
    ...actual,
    items: actual.items.map((i) => (i.articuloId === articuloId ? { ...i, cantidad: limpia } : i)),
  })
}

export function quitar(articuloId: string): void {
  const actual = leer()
  escribir({ ...actual, items: actual.items.filter((i) => i.articuloId !== articuloId) })
}

/** Quita varias líneas de una vez: lo usa el aviso de artículos no disponibles. */
export function quitarVarios(articuloIds: string[]): void {
  const actual = leer()
  const fuera = new Set(articuloIds)
  escribir({ ...actual, items: actual.items.filter((i) => !fuera.has(i.articuloId)) })
}

/**
 * Vacía las líneas y conserva el destino: faena y operador.
 *
 * La próxima orden casi siempre va al mismo lugar y con el mismo transportista,
 * así que volver a preguntarlo sería trabajo repetido. Las LÍNEAS sí se van:
 * esas ya se compraron.
 */
export function vaciar(): void {
  escribir({ ...leer(), items: [] })
}

/** Fija la faena. Una cadena sin forma de identificador equivale a ninguna. */
export function fijarFaena(faenaId: string | null): void {
  const limpio = faenaId !== null && pareceId(faenaId) ? faenaId.trim() : null
  escribir({ ...leer(), faenaId: limpio })
}

/** Fija el operador logístico. Mismo criterio de forma que la faena. */
export function fijarOperador(operadorId: string | null): void {
  const limpio = operadorId !== null && pareceId(operadorId) ? operadorId.trim() : null
  escribir({ ...leer(), operadorId: limpio })
}

/**
 * Olvida la faena guardada si el servidor no la acepta.
 *
 * Hace falta porque el carrito sobrevive al cierre de sesión —vive en
 * `localStorage`, no en la cuenta— y dos personas pueden usar el mismo
 * navegador. La faena de la primera no le pertenece a la segunda, y el backend
 * responde 404 (no 403: confirmar la existencia ya sería una fuga). Sin esto,
 * la faena ajena viajaría en `?faenaId=` del catálogo y dejaría a la segunda
 * persona mirando un error en la vitrina por un dato accesorio.
 *
 * También se olvida con `validacion`, pero SOLO si el detalle culpa a
 * `faenaId`: una faena con forma imposible da 400, no 404, y mirar solo el 404
 * dejaba la vitrina rota de forma PERMANENTE —el valor malo seguía guardado,
 * así que recargar repetía el error—. El detalle importa porque el catálogo
 * tiene otros campos que dan 400 —una búsqueda de más de 120 caracteres, por
 * ejemplo—, y borrar el destino de despacho por un error ajeno sería peor que
 * el problema que se está resolviendo.
 *
 * Se cuelga del catálogo, que se carga en casi toda visita, y no de la ficha de
 * producto, donde un 404 también puede significar que el producto no existe.
 */
export function useOlvidarFaenaInvalida(error: unknown, faenaId: string | null): void {
  useEffect(() => {
    if (!faenaId) return
    if (!(error instanceof ErrorApi)) return
    const laCulpaEsDeLaFaena =
      error.codigo === 'no_encontrado' ||
      (error.codigo === 'validacion' && error.detalleDe('faenaId') !== undefined)
    if (laCulpaEsDeLaFaena) fijarFaena(null)
  }, [error, faenaId])
}
